// src/app/core/interceptors/token.interceptor.ts
import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { ToastController, AlertController, NavController } from '@ionic/angular';
import { UserService } from 'app/services/user.service';
import { Observable, BehaviorSubject, of, Subject, throwError } from 'rxjs';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { catchError, filter, finalize, map, switchMap, take, timeout } from 'rxjs/operators';
import { AuthRefreshTokenResultModel } from 'app/models/authentication/authRefreshTokenResult.model';
import { Constants } from 'app/utility/constants';


const CONSTANTS = {
    TIMEOUT_MS: 30000,
    DEFAULT_RETRY_MS: 10000,
    JITTER_MS: 5000,
    MAX_RETRIES: 2,
    TOKEN_EXPIRY_MINUTES: 1
} as const;

const ERROR_MESSAGES = {
    SERVER_ERROR: 'Server Error: Unable to connect to the server.',
    TIMEOUT_ERROR: 'Server Error: Unable to receive a timely response from the server.',
    DATABASE_ERROR: 'Database version error',
    REAUTH_REQUIRED: 'Please re-authenticate',
    BUSY_SERVER: (seconds: number) => `Server is busy. Please retry after ${seconds} seconds...`
} as const;

const HTTP_STATUS = {
    UNAUTHORIZED: 401,
    TOO_MANY_REQUESTS: 429,
    GATEWAY_TIMEOUT: 504
} as const;

const AUTH_URLS = [
    "/authentication/login",
    "/authentication/authorize",
    "/authentication/token",
    "/authentication/ping",
    "/authentication/logout",
    "/authentication/appversion"
] as const;

@Injectable()
export class TokenInterceptor implements HttpInterceptor
{
    constructor(private userService: UserService, private router: Router, public toastController: ToastController, public alertController: AlertController) { }

    //private handleAuthError(err: HttpErrorResponse): Observable<any> {
    //    //handle your auth error or rethrow
    //    console.log('server error : ', err);
    //    if (err.status === 401 || err.status === 403) {
    //        //navigate /delete cookies or whatever
    //        localStorage.removeItem('currentUser');
    //        this.router.navigate(['/login']);
    //        // if you've caught / handled the error, you don't want to rethrow it unless you also want downstream consumers to have to handle it as well.
    //         return Observable.of(err.message);
    //        //return Observable.of(null);
    //    }
    //    else {
    //        return Observable.throw(err);
    //    }
    //}


    private _refreshSubject: BehaviorSubject<AuthRefreshTokenResultModel> = new BehaviorSubject<AuthRefreshTokenResultModel>(null);

    private _refreshTokenInProgress = false;
    private _refreshTokenQueueSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>>
    {

        if (req.url.toLowerCase().endsWith("/authentication/login") || req.url.toLowerCase().endsWith("/authentication/authorize") ||
            req.url.toLowerCase().endsWith("/authentication/token") || req.url.toLowerCase().endsWith("/authentication/ping") ||
            req.url.toLowerCase().endsWith("/authentication/logout") || req.url.toLowerCase().endsWith("/authentication/appversion")
        )
        {
            return next.handle(req);
        }
        else
        {
            try
            {
                req = this.updateHeader(req);
            }
            catch (error)
            {
                if (error instanceof TokenExpiredError)
                {
                    return this.handleTokenRefresh(req, next);
                }
            }

            return next.handle(req)
                .pipe(
                    catchError((error, caught) =>
                    {
                        if (error instanceof HttpErrorResponse)
                        {
                            if (this.checkTokenExpiryError(error))
                            {
                                return this.handleTokenRefresh(req, next);
                            }
                            else
                            {
                                return this.handleError(error, false);
                            }
                        }
                        console.log("returning caught");
                        return caught;
                    })
                ) as Observable<HttpEvent<any>>
        }
    }

    private handleTokenRefresh(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>
    {
        if (this._refreshTokenInProgress)
        {
            // If refreshTokenInProgress is true, we will wait until refreshTokenSubject has a non-null value
            // which means the new token is ready and we can retry the request again
            return this._refreshTokenQueueSubject.pipe(
                filter(result => result !== null),
                take(1),
                switchMap(() => next.handle(this.updateHeader(req)))
            );
        }
        else
        {
            this._refreshTokenInProgress = true;

            // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
            this._refreshTokenQueueSubject.next(null);

            return this.requestAccessToken().pipe(
                timeout(Constants.ApiLongTimeout),
                filter(result => result !== null),
                take(1),
                switchMap((x) =>
                {
                    // Remove the stale listeners by recreating the subject
                    this._refreshSubject = new BehaviorSubject<AuthRefreshTokenResultModel>(null);
                    return next.handle(this.updateHeader(req));
                }),
                // When the call to refreshToken completes we reset the refreshTokenInProgress to false
                catchError(err => {
                    // Handle timeout or other errors
                    console.error('Token refresh failed or timed out:', err);
                    return throwError(() => new Error(err));
                }),
                // for the next time the token needs to be refreshed
                finalize(() => {
                    // console.debug('Freeing up interceptor queue');
                    this._refreshTokenInProgress = false;
                    this._refreshTokenQueueSubject.next(true); // Ensure unblocking even on finalize
                })
            );
        }
    }

    private handleError(err: HttpErrorResponse, logout: boolean = false)
    {
        if (err instanceof HttpErrorResponse)
        {
            if (err.status === 0)
            {
                this.showToastAlert('Server Error: Unable to connect to the server.');
                return throwError(err);
            }

            if (err.status === HTTP_STATUS.TOO_MANY_REQUESTS)
            {
                return this.handleTooManyRequestsError(err);
            }

            if (err.status == 504)
            {
                this.showToastAlert('Server Error: Unable to receive a timely response from the server.');
                return throwError(err);
            }

            if (err.status == 401)
            {
                console.error(err);
                if (err.message == "Database does not match current session")
                {
                    console.warn("Database version error");
                    this.showToastAlert("Database version error");
                }
                else
                {
                    console.warn("Session expired - need to re-authenticate");
                    this.showToastAlert("Please re-authenticate", "primary");
                }

                this.performLogout();
            }
            else
            {
                if (err.error && err.error.entityDataJson)
                {
                    const dtoError = JSON.parse(err.error.entityDataJson);
                    console.error(dtoError);
                }

                if (logout)
                {
                    this.performLogout();
                }

                return throwError(err);
            }
        }

        if (this._refreshTokenInProgress)
        {
            console.debug('Freeing up interceptor queue in handleError');
            this._refreshTokenInProgress = false;
            this._refreshSubject = new BehaviorSubject<AuthRefreshTokenResultModel>(null);
            this._refreshTokenQueueSubject.next(true);
        }
    }

    private performLogout()
    {
        this.userService.logout();

        if (document.location.href.indexOf('/login') == -1)
        {
            // Redirect to the login page if not currently on it
            this.router.navigateByUrl('/login');
        }
    }

    private requestAccessToken()
    {
        this._refreshSubject.subscribe({
            complete: () =>
            {
                this._refreshSubject = new BehaviorSubject<AuthRefreshTokenResultModel>(null);
            }
        });
        if (this._refreshSubject.observers.length === 1)
        {
            this.userService.getNewAccessTokenObservable()
                .pipe(timeout(Constants.ApiShortTimeout))
                .subscribe({next: x => this._refreshSubject.next(x), error: err => this.handleError(err, true)});
        }
        return this._refreshSubject;
    }

    private checkTokenExpiryError(error: HttpErrorResponse): boolean
    {
        var isExpired = (
            error.status &&
            error.status === 401 &&
            this.userService.getAccessToken() != null
        );

        return isExpired;
    }

    private updateHeader(req: HttpRequest<any>): HttpRequest<any>
    {
        const authToken = this.userService.getAccessToken();
        if (this.isTokenExpiring(authToken.tokenExpirationUtc))
        {
            throw new TokenExpiredError();
        }
        return req.clone({
            setHeaders: {
                Authorization: `Bearer ${authToken.token}`
            }
        });
    }

    private isTokenExpiring(tokenExpirationUtc: Date): boolean
    {
        const now = new Date();
        const expiration = new Date(tokenExpirationUtc);

        const timeDifference = expiration.getTime() - now.getTime();
        const minutesUntilExpiration = timeDifference / (1000 * 60);
        const expiring = minutesUntilExpiration < CONSTANTS.TOKEN_EXPIRY_MINUTES;
        // if (expiring)
        // {
        //     console.warn(`Token is expiring in ${minutesUntilExpiration} minutes`);
        // }
        return expiring;
    }

    async showToastAlert(message: string, color: string = "danger", duration: number = 3000)
    {
        const toast = await this.toastController.create({
            message: message,
            duration: 3000,
            buttons: [
                {
                    side: 'end',
                    icon: 'close-circle-outline',
                    role: 'cancel',
                    handler: () =>
                    {
                        toast.dismiss();
                    }
                }
            ],
            position: 'bottom',
            color: color
        });
        toast.present();
    }

    async presentAlert(title, message)
    {
        const alert = await this.alertController.create({
            header: title,
            message: message,
            buttons: [
                {
                    text: 'Reload',
                    role: 'cancel',
                    cssClass: 'secondary',
                    handler: (blah) =>
                    {
                        location.reload();
                    }
                },
                {
                    text: 'Logout',
                    handler: () =>
                    {
                        this.userService.logout();
                    }
                }
            ]
        });

        await alert.present();
    }

    private handleTooManyRequestsError(error: HttpErrorResponse): Observable<HttpEvent<unknown>>
    {
        const retryAfter = error.headers.get('Retry-After');
        const retryAfterMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : CONSTANTS.DEFAULT_RETRY_MS;

        //for the time being, we won't automatically retry the request, we'll just show an alert
        this.presentAlert('Request Failed', ERROR_MESSAGES.BUSY_SERVER(retryAfterMs / 1000));

        return throwError(error);
    }
}


class TokenExpiredError extends Error
{
    constructor()
    {
        super('Token is expiring');
    }
}
